import { getWindowKonvaObject } from '../../helpers/konva/getWindowKonvaObject';

/**
 * Export the current map as a PNG, framed to the editing zone, at high
 * resolution.
 *
 * Strategy: Konva's stage.toDataURL renders the live scene with whatever
 * transform is currently applied. To produce a stable, share-able image:
 *
 *   1. Save current pan/zoom + visibility of the editor's overlay layers.
 *   2. Reset stage scale to 1 and position to origin so (0, 0) lines up
 *      with the editing zone's top-left.
 *   3. Hide the first and last layers — those are editor chrome
 *      (editing-area outline, selection rect, transformer handles, grid,
 *      preview shapes). The middle layers are floor/background + shapes
 *      + spots, which are what we want to export.
 *   4. Capture the editing-zone rectangle at the requested pixel ratio.
 *   5. Restore everything in the same synchronous block so React/Konva
 *      never gets a chance to repaint mid-export and the user sees no
 *      flicker.
 */
export interface ExportMapOptions {
  width: number;
  height: number;
  /** Output pixel multiplier. 3× yields ~retina-grade output. */
  pixelRatio?: number;
  /** Filename for the downloaded PNG. Defaults to a timestamped name. */
  filename?: string;
  /** Background color drawn under the scene. White is the safest default for
   *  sharing into chat/doc tools that don't render transparency well. */
  background?: string;
}

// Largest canvas side most browsers will accept. Chrome/Firefox cap around
// 32k, but iOS Safari can be as low as 16k, so we use 16384 as the safe
// ceiling. Above this the browser silently returns a blank canvas.
const MAX_CANVAS_DIMENSION = 16384;

export async function exportMapAsPng(
  options: ExportMapOptions
): Promise<boolean> {
  const stage = getWindowKonvaObject().stages?.[0];
  if (!stage) return false;

  const {
    width,
    height,
    pixelRatio = 3,
    filename = `map-${Date.now()}.png`,
    background = '#FFFFFF'
  } = options;

  const layers = stage.getLayers();
  if (layers.length === 0) return false;

  // Cap pixelRatio so neither output dimension exceeds the browser limit.
  // Without this, large maps × pixelRatio 3 quietly produce a 0×0 canvas.
  const effectivePixelRatio = Math.min(
    pixelRatio,
    MAX_CANVAS_DIMENSION / width,
    MAX_CANVAS_DIMENSION / height
  );

  // Snapshot current state so we can restore — also captures per-layer
  // visibility in case the user has hidden a layer manually.
  const prevScale = stage.scale();
  const prevPos = stage.position();
  const layerVisibility = layers.map((l) => l.visible());

  // Editor chrome: first layer (editing-area outline) and last layer
  // (selection / transformer / grid / preview). Indices in between are
  // the actual floor/shapes/spots content we want to export.
  const editorChromeIdx = new Set<number>([0, layers.length - 1]);

  let intermediate: HTMLCanvasElement;
  try {
    layers.forEach((layer, i) => {
      if (editorChromeIdx.has(i)) layer.hide();
    });
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.draw();

    intermediate = stage.toCanvas({
      x: 0,
      y: 0,
      width,
      height,
      pixelRatio: effectivePixelRatio
    } as Parameters<typeof stage.toCanvas>[0]);
  } catch (err) {
    // Most likely cause: the canvas got tainted by an external image with
    // missing CORS headers. Surface the error to the caller; the toolbar
    // toast layer can show it.
    // biome-ignore lint/suspicious/noConsoleLog: caller-visible export
    console.error('exportMapAsPng failed', err);
    return false;
  } finally {
    // Restore the stage immediately — the captured canvas is independent
    // of the live stage so encoding can run after the user sees the editor
    // back to normal.
    layers.forEach((layer, i) => {
      if (layerVisibility[i]) layer.show();
      else layer.hide();
    });
    stage.scale(prevScale);
    stage.position(prevPos);
    stage.draw();
  }

  // Browsers silently return a 0-sized canvas when the requested dimensions
  // exceed their internal cap. Treat that as failure instead of producing
  // an empty download.
  if (intermediate.width === 0 || intermediate.height === 0) {
    // biome-ignore lint/suspicious/noConsoleLog: caller-visible export
    console.error(
      'exportMapAsPng failed: empty canvas, requested size exceeds browser limit'
    );
    return false;
  }

  // Composite the scene over an opaque background — Konva's output alone
  // would be transparent, which is messy in many sharing tools.
  const out = document.createElement('canvas');
  out.width = intermediate.width;
  out.height = intermediate.height;
  const ctx = out.getContext('2d');
  if (!ctx) return false;
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(intermediate, 0, 0);

  // toBlob avoids the base64 inflation and ~100 MB data-URL ceiling that
  // toDataURL hits on large maps.
  const blob = await new Promise<Blob | null>((resolve) => {
    out.toBlob((b) => resolve(b), 'image/png');
  });
  if (!blob) {
    // biome-ignore lint/suspicious/noConsoleLog: caller-visible export
    console.error('exportMapAsPng failed: toBlob returned null');
    return false;
  }

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } finally {
    URL.revokeObjectURL(url);
  }
}
